/**
 * Credential Management Modal
 * 
 * UI for managing credentials (API keys, usernames, passwords) for data source integrations
 * Supports secure storage and validation of credentials
 */

import React, { useState } from 'react';
import {
  Modal,
  Button,
  Input,
  Badge,
  LoadingSpinner,
} from '@tracekit/core';
import { Key, Eye, EyeOff, CheckCircle, XCircle, AlertTriangle, Lock } from 'lucide-react';
import { DataSourceType } from '../services/dataSourceRegistry';

export interface CredentialField {
  id: string;
  label: string;
  type: 'text' | 'password' | 'api_key';
  required: boolean;
  placeholder?: string;
  description?: string;
  value?: string;
}

export interface CredentialManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (credentials: Record<string, string>) => Promise<void>;
  source: DataSourceType;
  sourceName: string;
  credentialFields: CredentialField[];
  existingCredentials?: Record<string, string>;
}

export function CredentialManagementModal({
  isOpen,
  onClose,
  onSave,
  source,
  sourceName,
  credentialFields,
  existingCredentials = {},
}: CredentialManagementModalProps) {
  const [credentials, setCredentials] = useState<Record<string, string>>(existingCredentials);
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Initialize credentials from existing
  React.useEffect(() => {
    if (isOpen) {
      setCredentials(existingCredentials);
      setError(null);
      setValidationErrors({});
    }
  }, [isOpen, existingCredentials]);

  const togglePasswordVisibility = (fieldId: string) => {
    setShowPasswords(prev => ({
      ...prev,
      [fieldId]: !prev[fieldId],
    }));
  };

  const handleFieldChange = (fieldId: string, value: string) => {
    setCredentials(prev => ({
      ...prev,
      [fieldId]: value,
    }));
    // Clear validation error for this field
    if (validationErrors[fieldId]) {
      setValidationErrors(prev => {
        const next = { ...prev };
        delete next[fieldId];
        return next;
      });
    }
  };

  const validateCredentials = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (credentialFields && Array.isArray(credentialFields)) {
      credentialFields.forEach(field => {
        if (field.required && !credentials[field.id]?.trim()) {
          errors[field.id] = `${field.label} is required`;
        }
        // Additional validation for API keys (should be non-empty if provided)
        if (field.type === 'api_key' && credentials[field.id] && credentials[field.id].trim().length < 10) {
          errors[field.id] = 'API key appears to be too short';
        }
      });
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    setError(null);
    
    if (!validateCredentials()) {
      setError('Please fix validation errors before saving');
      return;
    }
    
    setSaving(true);
    try {
      await onSave(credentials);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save credentials');
    } finally {
      setSaving(false);
    }
  };

  const getFieldIcon = (field: CredentialField) => {
    if (field.type === 'api_key' || field.type === 'password') {
      return <Key className="h-4 w-4 text-gray-400" />;
    }
    return null;
  };

  const maskValue = (value: string, type: string): string => {
    if (!value) return '';
    if (type === 'password' || type === 'api_key') {
      return '•'.repeat(Math.min(value.length, 20));
    }
    return value;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Manage Credentials - ${sourceName}`}
      size="lg"
    >
      <div className="space-y-4">
        {/* Info Banner */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <Lock className="h-4 w-4 text-blue-600 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-blue-900 dark:text-blue-100 font-medium">
                Secure Credential Storage
              </p>
              <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                Credentials are encrypted and stored securely. They will be used for authentication when syncing data from this source.
              </p>
            </div>
          </div>
        </div>

        {/* Credential Fields */}
        <div className="space-y-4">
          {!credentialFields || !Array.isArray(credentialFields) || credentialFields.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <AlertTriangle className="h-12 w-12 mx-auto mb-3 text-yellow-500" />
              <p>No credential fields configured for this data source.</p>
            </div>
          ) : credentialFields.map(field => {
            const isPassword = field.type === 'password' || field.type === 'api_key';
            const showPassword = showPasswords[field.id] || false;
            const hasValue = !!credentials[field.id];
            const fieldError = validationErrors[field.id];
            const displayValue = hasValue && !showPassword 
              ? maskValue(credentials[field.id], field.type)
              : credentials[field.id] || '';

            return (
              <div key={field.id}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                {field.description && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    {field.description}
                  </p>
                )}
                <div className="relative">
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                    {getFieldIcon(field)}
                  </div>
                  <Input
                    type={isPassword && !showPassword ? 'password' : 'text'}
                    value={displayValue}
                    onChange={(e) => handleFieldChange(field.id, e.target.value)}
                    placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                    className={`pl-10 pr-10 ${fieldError ? 'border-red-500' : ''}`}
                    disabled={saving}
                  />
                  {isPassword && hasValue && (
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility(field.id)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  )}
                </div>
                {fieldError && (
                  <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                    <XCircle className="h-3 w-3" />
                    {fieldError}
                  </p>
                )}
                {hasValue && !fieldError && (
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" />
                    Credential provided
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5" />
              <p className="text-sm text-red-900 dark:text-red-100">{error}</p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Lock className="h-4 w-4 mr-2" />
                Save Credentials
              </>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
}


